import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';

import Input from '../../components/form/input';
import { compareTextTemplates } from '../../lib/textTemplateSort';

function isValidCellphone(input) {
    // Define a regular expression pattern for a valid cellphone number
    const pattern = /\d{8}$/; // Assumes a 8-digit number

    // Test the input against the pattern
    return pattern.test(input);
}

// Editable recipient may be typed with spaces, dashes or a +47 prefix. Strip the
// formatting before the 8-digit check (the backend normalizePhone() does the same),
// so a hand-typed number like "+47 915 20 191" still validates.
function isValidRecipient(input) {
    return isValidCellphone((input || '').replace(/[\s\-()+.]/g, ''));
}

// SMS provider (Target365) sender ID rules: letters/digits only (a-zA-Z0-9),
// no spaces or special chars, max 11 chars. Strip everything else and cap length.
function sanitizeSender(input) {
    return (input || '').replace(/[^a-zA-Z0-9]/g, '').slice(0, 11);
}

// Stable key for a recipient badge. The incoming users may come straight from a
// store list where some entries lack an id, so the cellphone is part of the key.
function recipientKey(user = {}) {
    return `${user.id || ''}|${user.cellphone || ''}`;
}

// Fill {{key}} merge fields (flettefelt) in a personalized SMS body. Only known
// keys are replaced — unknown placeholders are left visible so typos are caught
// before sending.
function fillMergeFields(template, fields = {}) {
    return (template || '').replace(/\{\{\s*([\wæøåÆØÅ]+)\s*\}\}/g, (match, key) => (
        fields[key] !== undefined && fields[key] !== null ? String(fields[key]) : match
    ));
}

const initialState = {};

@observer
class SendSms extends Component {
    constructor(props) {
        super(props);
        this.state = {
            ...initialState,
            subject: '',
            body: '',
            textTemplateSaved: {},
            textTemplates: [],
            showTemplateModal: false,
            templateModalSection: '',
            templateModalField: '',
            templateSearchQuery: '',
            selectedTemplatePreview: null,
            deleteTemplateConfirm: null,
            // Editable, provider-valid sender (only used when editableSender prop is set).
            // Seeded from the incoming `from`, sanitized to a-zA-Z0-9 / max 11 chars.
            sender: sanitizeSender(props.from),
            // Editable recipient (only used when editableRecipient prop is set). Seeded
            // from the first incoming recipient's cellphone so the user can redirect the
            // SMS to a different number at send time, e.g. when the animal owner is an
            // animal-welfare org whose registered number is not the right person.
            recipient: props.editableRecipient ? (props.users?.[0]?.cellphone || '') : '',
            // Recipients the user has removed from this send. Keys only — the incoming
            // `users` prop is a store list and must never be mutated here.
            removedRecipients: [],
        };
    }

    // Active recipients = everyone passed in, minus the ones removed in this drawer.
    getActiveUsers = () => {
        const { users = [] } = this.props;
        const { removedRecipients = [] } = this.state;
        if (removedRecipients.length === 0) {
            return users;
        }
        return users.filter(u => !removedRecipients.includes(recipientKey(u)));
    }

    removeRecipient = (e) => {
        const { key } = e.target.closest('button').dataset;
        const { removedRecipients = [] } = this.state;
        if (removedRecipients.includes(key)) {
            return;
        }
        this.setState({ removedRecipients: [...removedRecipients, key] });
    }

    restoreRecipient = (e) => {
        const { key } = e.target.closest('button').dataset;
        const { removedRecipients = [] } = this.state;
        this.setState({ removedRecipients: removedRecipients.filter(k => k !== key) });
    }

    restoreAllRecipients = () => {
        this.setState({ removedRecipients: [] });
    }

    loadAll = async () => {
        const { textTemplateStore, appState } = this.props.stores;
        const tpls = await textTemplateStore.load({ query: { section: 'sendSms' }, skipUpdate: 1 });
        this.setState({ textTemplates: tpls || [] });

        const { message } = this.props;
        if (message) {
            appState.updateObjectKeyValue('newSms', 'body', message);
        }
    }

    sendSms = async () => {
        this.setState({
            inProgress: true,
        });
        const { appState, userStore } = this.props.stores;
        const { customer } = userStore;
        const { smsSender = 'RaskePoter' } = customer;
        const { from = smsSender, editableSender, editableRecipient, personalized } = this.props;
        const { newSms = {} } = appState;
        // Recipients removed in the drawer are left out of every send path below.
        const users = this.getActiveUsers();

        // When the sender is editable, use the validated value from state and require it.
        const effectiveFrom = editableSender ? this.state.sender : from;
        if (editableSender && !effectiveFrom) {
            this.setState({
                errorTmp: 'Avsender mangler.',
                inProgress: false,
            });
            return;
        }

        // When the recipient is editable, send to the single typed number (validated);
        // otherwise keep the default behaviour of sending to every passed-in user.
        const effectiveTo = editableRecipient
            ? (isValidRecipient(this.state.recipient) ? [this.state.recipient.trim()] : [])
            : users.filter(u => isValidCellphone(u.cellphone)).map(u => u.cellphone);
        if (editableRecipient && effectiveTo.length === 0) {
            this.setState({
                errorTmp: 'Mottakernummer mangler eller er ugyldig.',
                inProgress: false,
            });
            return;
        }
        if (!editableRecipient && effectiveTo.length === 0) {
            this.setState({
                errorTmp: 'Ingen mottakere igjen. Legg til minst én mottaker.',
                inProgress: false,
            });
            return;
        }

        if (!newSms.body) {
            this.setState({
                errorTmp: 'Error! Please fill in all fields.',
                inProgress: false,
            });
            return;
        }

        // Personalized mode: each recipient gets their own body with the
        // {{flettefelt}} filled from user.mergeFields, so the SMS is sent one
        // by one instead of as a single broadcast.
        if (personalized) {
            const recipients = users.filter(u => isValidCellphone(u.cellphone));
            if (recipients.length === 0) {
                this.setState({
                    errorTmp: 'Ingen mottakere med gyldig telefonnummer.',
                    inProgress: false,
                });
                return;
            }
            let failed = 0;
            for (let i = 0; i < recipients.length; i += 1) {
                const recipientUser = recipients[i];
                // eslint-disable-next-line no-await-in-loop
                const personalizedResponse = await appState.sendSms({
                    from: effectiveFrom,
                    to: [recipientUser.cellphone],
                    body: fillMergeFields(newSms.body, recipientUser.mergeFields),
                });
                if (!personalizedResponse || personalizedResponse.status !== 200) {
                    failed += 1;
                }
            }
            if (failed === 0) {
                appState.updateKeyValue('newSms', {});
                this.setState({
                    message: 'Sms sent!',
                    inProgress: false,
                });
                setTimeout(() => {
                    appState.toggleDrawer();
                }, 1000);
            } else {
                this.setState({
                    error: `Klarte ikke å sende ${failed} av ${recipients.length} SMS. Prøv igjen senere.`,
                    inProgress: false,
                });
            }
            return;
        }

        const response = await appState.sendSms({
            // from: '',
            from: effectiveFrom,
            to: effectiveTo,
            body: newSms.body,
        })

        if (response.status === 200) {
            appState.updateKeyValue('newSms', {});
            this.setState({
                message: 'Sms sent!',
                inProgress: false,
            });
            setTimeout(() => {
                appState.toggleDrawer();
            }, 1000);
        } else {
            this.setState({
                error: 'Error! Could not send sms. Please try again later.',
                inProgress: false,
            });
        }
    }

    saveTextTemplate = async (e) => {
        const { textTemplateStore, appState } = this.props.stores;
        const { section, field } = e.target.closest('button').dataset;
        const { newSms = {} } = appState;
        const response = await textTemplateStore.insert({
            section,
            field,
            body: newSms[field],
        });
        if (response.status === 201) {
            const { textTemplateSaved } = this.state;
            textTemplateSaved[`${field}`] = true;
            this.setState({ textTemplateSaved });
            setTimeout(() => {
                const { textTemplateSaved } = this.state;
                delete textTemplateSaved[`${field}`];
                this.setState({ textTemplateSaved });
            }, 3000);
        }
    }

    openTemplateModal = (section, field) => {
        this.setState({
            showTemplateModal: true,
            templateModalSection: section,
            templateModalField: field,
            templateSearchQuery: '',
            selectedTemplatePreview: null,
            deleteTemplateConfirm: null,
        });
    }

    closeTemplateModal = () => {
        this.setState({
            showTemplateModal: false,
            templateModalSection: '',
            templateModalField: '',
            templateSearchQuery: '',
            selectedTemplatePreview: null,
            deleteTemplateConfirm: null,
        });
    }

    handleTemplateSearch = (e) => {
        this.setState({ templateSearchQuery: e.target.value });
    }

    selectTemplateFromModal = (template) => {
        this.setState({
            selectedTemplatePreview: template,
            deleteTemplateConfirm: null,
        });
    }

    applySelectedTemplate = () => {
        const { selectedTemplatePreview, templateModalField } = this.state;
        if (!selectedTemplatePreview || !templateModalField) {
            return;
        }

        const { appState } = this.props.stores;
        // Keep the SMS picker's established behaviour: applying a template replaces
        // the draft instead of appending it to a default or previously typed message.
        appState.updateObjectKeyValue('newSms', templateModalField, selectedTemplatePreview.body);
        this.closeTemplateModal();
    }

    deleteTextTemplate = async () => {
        const { textTemplateStore } = this.props.stores;
        const { deleteTemplateConfirm, textTemplates } = this.state;
        if (!deleteTemplateConfirm) {
            return;
        }
        await textTemplateStore.delete(parseInt(deleteTemplateConfirm, 10));
        // Templates here live in component state (loaded with skipUpdate), so drop the
        // deleted one locally and reset the modal preview.
        this.setState({
            textTemplates: textTemplates.filter(t => t.id !== parseInt(deleteTemplateConfirm, 10)),
            deleteTemplateConfirm: null,
            selectedTemplatePreview: null,
        });
    }

    handleSenderInput = (e) => {
        // Live-strip to provider-valid chars and cap at 11 so the field can never
        // hold an invalid sender.
        this.setState({ sender: sanitizeSender(e.target.value) });
    }

    handleRecipientInput = (e) => {
        // Keep the raw input (digits, spaces, +47) — validation/normalization of the
        // recipient happens on send (isValidRecipient + backend normalizePhone).
        this.setState({ recipient: e.target.value });
    }

    componentDidMount() {
        this.loadAll();
    }

    render() {
        const {
            error,
            errorTmp,
            message,
            inProgress,
            textTemplateSaved,
            textTemplates,
            sender,
            recipient,
            showTemplateModal,
            templateModalSection,
            templateModalField,
            templateSearchQuery,
            selectedTemplatePreview,
            deleteTemplateConfirm,
            removedRecipients = [],
        } = this.state;
        const { from, users: allUsers = [], hideTemplateOption, editableSender, editableRecipient, personalized } = this.props;
        // Only the recipients still selected are shown, previewed and sent to.
        const users = this.getActiveUsers();
        const removedUsers = removedRecipients.length > 0
            ? allUsers.filter(u => removedRecipients.includes(recipientKey(u)))
            : [];
        const validUsers = users.filter(u => isValidCellphone(u.cellphone));
        const invalidUsers = users.filter(u => !isValidCellphone(u.cellphone));
        const recipientName = `${allUsers?.[0]?.firstname || ''} ${allUsers?.[0]?.lastname || ''}`.trim();
        const { appState } = this.props.stores;
        const { newSms = {} } = appState;

        // Personalized mode: list the available flettefelt and preview the first
        // recipient's rendered message so the admin can verify before sending.
        const mergeFieldKeys = personalized
            ? [...new Set(users.flatMap(u => Object.keys(u.mergeFields || {})))]
            : [];
        const previewUser = personalized ? validUsers[0] : null;
        const previewBody = previewUser ? fillMergeFields(newSms.body, previewUser.mergeFields) : '';

        const textTpls = textTemplates
            .filter(t => t.section === 'sendSms' && t.field === 'body')
            .sort(compareTextTemplates);
        const visibleTextTpls = textTemplates
            .filter(t =>
                t.section === templateModalSection &&
                t.field === templateModalField &&
                (templateSearchQuery === '' ||
                 t.title?.toLowerCase().includes(templateSearchQuery.toLowerCase()) ||
                 t.body?.toLowerCase().includes(templateSearchQuery.toLowerCase()))
            )
            .sort(compareTextTemplates);

        return (<>
            <div class='d-flex flex-column justify-content-start overflow-auto mb-5 mt-3 p-4'>
                <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                    <Text id='race.admin.send-sms-title'>Send Sms</Text>
                </div>

                {/* <xmp>{JSON.stringify(users, null, 4)}</xmp> */}
                {editableSender ? (
                    <div class='d-flex flex-column px-2 mb-1'>
                        <div class='d-flex flex-row align-items-center'>
                            <span class='me-2 text-muted'>
                                <Text id='input.from'>From</Text>
                            </span>
                            <input
                                type='text'
                                class='form-control form-control-sm'
                                style='max-width: 160px;'
                                maxLength={11}
                                value={sender}
                                onInput={this.handleSenderInput}
                                placeholder='Avsender'
                                autoComplete='off'
                            />
                        </div>
                        {sender
                            ? <small class='text-muted mt-1'>
                                <Text id='input.sms-sender-help'>Only a–z, A–Z, 0–9 · max 11 chars</Text>
                            </small>
                            : <small class='text-danger mt-1'>
                                <Text id='input.sms-sender-required'>Sender required</Text>
                            </small>}
                    </div>
                ) : (
                    <div class='d-flex flex-row justify-content-start px-2'>
                        {from && <>
                            <span class='me-2 text-muted'>
                                <Text id='input.from'>From</Text>
                            </span>
                            <span class={`badge text-bg-secondary font-weight-normal me-1 mt-1`}>
                                {from}
                            </span>
                        </>}
                    </div>
                )}

                {editableRecipient ? (
                    <div class='d-flex flex-column px-2 mb-1'>
                        <div class='d-flex flex-row align-items-center'>
                            <span class='me-2 text-muted'>
                                <Text id='input.to'>To</Text>
                            </span>
                            <input
                                type='tel'
                                class='form-control form-control-sm'
                                style='max-width: 200px;'
                                value={recipient}
                                onInput={this.handleRecipientInput}
                                placeholder='+47 ...'
                                autoComplete='off'
                            />
                        </div>
                        {recipientName && <small class='text-muted mt-1'>
                            <Text id='input.sms-recipient-registered'>Registered</Text>: {recipientName}
                        </small>}
                        {isValidRecipient(recipient)
                            ? <small class='text-muted mt-1'>
                                <Text id='input.sms-recipient-help'>The number that will receive the SMS</Text>
                            </small>
                            : <small class='text-danger mt-1'>
                                <Text id='input.sms-recipient-required'>Valid recipient number required</Text>
                            </small>}
                    </div>
                ) : (<>
                    <div class='d-flex flex-wrap justify-content-start px-2'>
                        <span class='me-2 text-muted'>
                            <Text id='input.to'>To</Text> ({validUsers.length})
                        </span>
                        {validUsers.map(user => {
                            return (
                                <Localizer key={recipientKey(user)}>
                                    <button
                                        type='button'
                                        data-key={recipientKey(user)}
                                        onClick={this.removeRecipient}
                                        class={`badge text-bg-secondary border-0 font-weight-normal me-1 mt-1`}
                                        title={<Text id='input.sms-remove-recipient'>Remove recipient</Text>}
                                    >
                                        {user.firstname} {user.lastname} &lt;{user.cellphone}&gt;
                                        <i class='fa-solid fa-xmark ms-2' />
                                    </button>
                                </Localizer>
                            );
                        })}
                    </div>
                    {validUsers.length === 0 && <div class='px-2 mt-1'>
                        <small class='text-danger'>
                            <Text id='input.sms-no-recipients-left'>No recipients left. Add at least one recipient.</Text>
                        </small>
                    </div>}
                    {invalidUsers.length > 0 && <>
                        <div class='d-flex flex-wrap justify-content-start px-2'>
                            <span class='me-2 text-muted'>
                                <Text id='input.invalid-cellphones'>Invalid cellphones</Text>
                            </span>
                            {invalidUsers.map(user => {
                                return (
                                    <span
                                        key={recipientKey(user)}
                                        class={`badge text-bg-danger font-weight-normal me-1 mt-1`}
                                    >
                                        {user.firstname} {user.lastname} &lt;{user.cellphone}&gt;
                                    </span>
                                );
                            })}
                        </div>
                    </>}
                    {removedUsers.length > 0 && <>
                        <div class='d-flex flex-wrap justify-content-start align-items-center px-2 mt-2'>
                            <span class='me-2 text-body-secondary'>
                                <Text id='input.sms-removed-recipients' fields={{ count: removedUsers.length }}>
                                    Removed ({removedUsers.length})
                                </Text>
                            </span>
                            <button
                                type='button'
                                class='btn btn-link btn-sm p-0 me-2'
                                onClick={this.restoreAllRecipients}
                            >
                                <i class='fa-duotone fa-rotate-left me-1' />
                                <Text id='input.sms-restore-all-recipients'>Undo all</Text>
                            </button>
                            {removedUsers.map(user => {
                                return (
                                    <Localizer key={recipientKey(user)}>
                                        <button
                                            type='button'
                                            data-key={recipientKey(user)}
                                            onClick={this.restoreRecipient}
                                            class={`badge bg-body-secondary text-body-secondary border-0 font-weight-normal me-1 mt-1 text-decoration-line-through`}
                                            title={<Text id='input.sms-restore-recipient'>Add recipient back</Text>}
                                        >
                                            {user.firstname} {user.lastname} &lt;{user.cellphone}&gt;
                                            <i class='fa-solid fa-plus ms-2' />
                                        </button>
                                    </Localizer>
                                );
                            })}
                        </div>
                    </>}
                </>)}

                <Localizer>
                    <Input
                        classNames='form-control rounded-lg'
                        title={<Text id='input.sms-body'>Sms content</Text>}
                        placeholder={<Text id='input.sms-body'>Sms content</Text>}
                        help={<Text id='input.sms-body-help'>Your sms content. Do NOT include links! SMS providers will stop the SMS from beeing sent.</Text>}
                        stores={this.props.stores}
                        holdingStore={appState}
                        object={newSms}
                        objectName={'newSms'}
                        type={'textarea'}
                        field={'body'}
                        value={newSms.body}
                        extraDataset={{
                            'data-isnew': true,
                        }}
                    />
                </Localizer>

                {personalized && <>
                    {mergeFieldKeys.length > 0 && <div class='px-2'>
                        <small class='text-body-secondary'>
                            Flettefelt: {mergeFieldKeys.map(k => `{{${k}}}`).join(', ')} — fylles inn per mottaker.
                        </small>
                    </div>}
                    {previewUser && newSms.body && <div class='px-2 mt-2'>
                        <small class='text-body-secondary'>
                            Forhåndsvisning ({previewUser.firstname} {previewUser.lastname}):
                        </small>
                        <div class='card mt-1'>
                            <div class='card-body py-2 px-3' style='font-size: 0.9rem; line-height: 1.3; white-space: pre-wrap;'>
                                {previewBody}
                            </div>
                        </div>
                    </div>}
                </>}

                {!hideTemplateOption && <div class='d-flex flex-row justify-content-end align-items-end'>
                    <button
                        class='btn btn-link btn-sm text-muted'
                        onClick={this.saveTextTemplate}
                        data-section={'sendSms'}
                        data-field={'body'}
                    >
                        <i class='fa-duotone fa-floppy-disk' /> <Text id='input.save-text-template'>Save as template</Text>
                    </button>
                    {textTemplateSaved.body && <>
                        <div class='alert alert-success' role='alert'>
                            <Text id='input.text-template-saved'>Text template saved</Text>
                        </div>
                    </>}
                </div>}
                {textTpls.length > 0 && <>
                    <div class='mb-2 d-flex flex-row justify-content-end align-items-end'>
                        <button
                            type='button'
                            class='btn btn-sm btn-outline-primary rounded-pill px-3'
                            onClick={() => this.openTemplateModal('sendSms', 'body')}
                        >
                            <i class='fa-duotone fa-text-size me-2' />
                            <Text id='input.choose-text-template'>Choose text template</Text>
                            <span class='badge bg-primary ms-2'>{textTpls.length}</span>
                        </button>
                    </div>
                </>}
                {showTemplateModal && <>
                    <div class='modal show d-block' tabindex='-1' style='background: rgba(0,0,0,0.5);'>
                        <div class='modal-dialog modal-lg modal-dialog-scrollable'>
                            <div class='modal-content'>
                                <div class='modal-header'>
                                    <h5 class='modal-title'>
                                        <i class='fa-duotone fa-text-size me-2' />
                                        <Text id='input.choose-text-template'>Choose text template</Text>
                                    </h5>
                                    <button type='button' class='btn-close' onClick={this.closeTemplateModal} />
                                </div>
                                <div class='modal-body'>
                                    <div class='row'>
                                        <div class='col-md-6'>
                                            <div class='mb-3'>
                                                <Localizer>
                                                    <input
                                                        type='text'
                                                        class='form-control'
                                                        placeholder={
                                                            <Text id='texttemplate.search-placeholder'>
                                                                Search for templates...
                                                            </Text>
                                                        }
                                                        value={templateSearchQuery}
                                                        onInput={this.handleTemplateSearch}
                                                    />
                                                </Localizer>
                                            </div>
                                            <div class='list-group' style='max-height: 400px; overflow-y: auto;'>
                                                {visibleTextTpls.map(t => (
                                                    <button
                                                        key={t.id}
                                                        type='button'
                                                        class={`list-group-item list-group-item-action ${
                                                            selectedTemplatePreview?.id === t.id ? 'active' : ''
                                                        }`}
                                                        onClick={() => this.selectTemplateFromModal(t)}
                                                    >
                                                        {t.title && <small class='text-muted'>{t.title}</small>}
                                                        <div style='font-size: 13px; line-height: 1.3;'>
                                                            {t.body?.substring(0, 80)}{t.body?.length > 80 ? '...' : ''}
                                                        </div>
                                                        <small class='text-muted'>
                                                            {util.formatDate(t.createdDate, { locale: 'nb-NO' })}
                                                        </small>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div class='col-md-6'>
                                            <h6 class='mb-2'>
                                                <Text id='input.template-preview'>Preview</Text>
                                            </h6>
                                            {selectedTemplatePreview ? (
                                                <div class='card'>
                                                    <div class='card-body'>
                                                        <div class='mb-2'>
                                                            <small class='text-muted'>
                                                                <i class='fa-duotone fa-calendar me-1' />
                                                                {util.formatDate(selectedTemplatePreview.createdDate, {
                                                                    locale: 'nb-NO',
                                                                })}
                                                            </small>
                                                        </div>
                                                        <div
                                                            class='card-text font-style-narrow'
                                                            style='font-size: 0.9rem; line-height: 1.2rem; white-space: pre-wrap;'
                                                        >
                                                            {selectedTemplatePreview.body}
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div class='text-muted text-center py-5'>
                                                    <i class='fa-duotone fa-file-lines fa-3x mb-3' />
                                                    <p>
                                                        <Text id='input.select-template-to-preview'>
                                                            Select a template to preview
                                                        </Text>
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div class='modal-footer'>
                                    {selectedTemplatePreview && <div class='me-auto'>
                                        {deleteTemplateConfirm === selectedTemplatePreview.id
                                            ? <>
                                                <button
                                                    type='button'
                                                    class='btn btn-sm btn-danger me-2'
                                                    onClick={this.deleteTextTemplate}
                                                >
                                                    <i class='fa-duotone fa-trash me-1' />
                                                    <Text id='input.delete-text-template-q'>
                                                        Delete this template?
                                                    </Text>
                                                </button>
                                                <button
                                                    type='button'
                                                    class='btn btn-sm btn-outline-secondary'
                                                    onClick={() => this.setState({ deleteTemplateConfirm: null })}
                                                >
                                                    <Text id='input.cancel-delete'>Cancel</Text>
                                                </button>
                                            </>
                                            : <button
                                                type='button'
                                                class='btn btn-sm btn-outline-danger'
                                                onClick={() => this.setState({
                                                    deleteTemplateConfirm: selectedTemplatePreview.id,
                                                })}
                                            >
                                                <i class='fa-duotone fa-trash me-1' />
                                                <Text id='input.delete-text-template'>Delete template</Text>
                                            </button>}
                                    </div>}
                                    <button
                                        type='button'
                                        class='btn btn-secondary rounded-pill'
                                        onClick={this.closeTemplateModal}
                                    >
                                        <Text id='input.cancel'>Cancel</Text>
                                    </button>
                                    <button
                                        type='button'
                                        class='btn btn-primary rounded-pill'
                                        onClick={this.applySelectedTemplate}
                                        disabled={!selectedTemplatePreview}
                                    >
                                        <i class='fa-duotone fa-check me-2' />
                                        <Text id='input.use-template'>Use Template</Text>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>}

                <div class='d-flex flex-column justify-content-center mt-5'>
                    <button
                        type='button'
                        class={`btn btn-block btn-primary rounded-pill`}
                        onClick={this.sendSms}
                        disabled={error || message || (editableSender && !sender)
                            || (editableRecipient ? !isValidRecipient(recipient) : validUsers.length === 0)}
                    >
                        <i class='fa-solid fa-paper-plane me-1' /> Send sms
                        {inProgress && <>
                            <span class='spinner-border spinner-border-sm ms-2' role='status' aria-hidden='true' />
                        </>}
                    </button>

                    {error && <div class='w-100 d-flex px-3 mt-4 justify-content-center'>
                        <div class='alert alert-danger rounded-pill' role='alert'>
                            {error}
                        </div>
                    </div>}
                    {errorTmp && <div class='w-100 d-flex px-3 mt-4 justify-content-center'>
                        <div class='alert alert-danger rounded-pill' role='alert'>
                            {errorTmp}
                        </div>
                    </div>}
                    {message && <div class='w-100 d-flex px-3 mt-4 justify-content-center'>
                        <div class='alert alert-success rounded-pill' role='alert'>
                            {message}
                        </div>
                    </div>}
                </div>
            </div>
        </>);
    }
}

export default SendSms;
